import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
  FormDescription
} from '@/components/ui/form';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Region, createRegionSchema, updateRegionSchema } from '@/features/regions/types';

// Form props for creating a new region
interface RegionFormProps {
  providerId?: number;
  region?: Region;
  onSubmit: (data: any) => void;
  isSubmitting: boolean;
}

export function RegionForm({ providerId, region, onSubmit, isSubmitting }: RegionFormProps) {
  // Determine if we're editing or creating
  const isEditing = !!region;
  
  // Use appropriate schema
  const schema = isEditing 
    ? updateRegionSchema 
    : createRegionSchema;
  
  // Initialize form with default values
  const form = useForm({
    resolver: zodResolver(schema),
    defaultValues: {
      name: region?.name || '',
      zip_code: region?.zip_code || '',
      provider_id: providerId || region?.provider_id
    }
  });

  // Handle form submission
  const handleSubmit = (data: any) => {
    onSubmit({
      ...data,
      // Ensure zip_code is undefined if empty (not null or empty string)
      zip_code: data.zip_code?.trim() || undefined
    });
  };

  return (
    <Form {...form}>
      <form onSubmit={form.handleSubmit(handleSubmit)} className="space-y-6">
        {/* Region Name */}
        <FormField
          control={form.control}
          name="name"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Region Name*</FormLabel>
              <FormControl>
                <Input placeholder="Enter region name" {...field} />
              </FormControl>
              <FormMessage />
              <FormDescription>
                A unique name for this region within the provider.
              </FormDescription>
            </FormItem>
          )}
        />

        {/* Zip Code */}
        <FormField
          control={form.control}
          name="zip_code"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Zip Code</FormLabel>
              <FormControl>
                <Input placeholder="e.g. 10001 or 10001-5555" {...field} value={field.value ?? ''} />
              </FormControl>
              <FormMessage />
              <FormDescription>
                Optional: Add a zip code to associate with this region. This helps with location-based services.
              </FormDescription>
            </FormItem>
          )}
        />

        {/* Provider ID (hidden if provided) */}
        {!isEditing && (
          <input type="hidden" {...form.register('provider_id')} />
        )}

        {/* Submit button */}
        <Button 
          type="submit" 
          className="w-full" 
          disabled={isSubmitting}
        >
          {isSubmitting ? 'Processing...' : isEditing ? 'Update Region' : 'Create Region'}
        </Button>
      </form>
    </Form>
  );
}
